/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

/* eslint-disable no-underscore-dangle */
import { createSelector } from '@reduxjs/toolkit';
import { SLICE } from 'modules/dashboard/constants';
import { initialState } from './slice';

/**
 * Getting entire users state into the component
 */
const selectDomain = (state) => state[SLICE.USERS] || initialState;
/**
 * Getting the loader state into the component
 */
export const selectLoader = createSelector([selectDomain], (usersState) => usersState.loading);
/**
 * Getting the users list state into the component
 */
export const selectUsers = createSelector([selectDomain], (usersState) => usersState?.usersList);
/**
 * Getting the roles list state into the component
 */
export const selectRoleList = createSelector([selectDomain], (usersState) => usersState.roleList);
/**
 * Getting the filtered user by user id state into the component
 */
const getFirstParam = (_, param) => param;
export const selectUserById = createSelector(
  [selectUsers, getFirstParam],
  (filteredUsers, userId) => filteredUsers?.results?.filter((user) => user.id === userId)[0]
);
/**
 * Getting the user hotels list state into the component
 */
export const selectUserHotelsList = createSelector(
  [selectDomain],
  (usersState) => usersState.hotelsList?.results
);
/**
 * Getting the updated user state into the component
 */
export const selectUpdatedUser = createSelector(
  [selectDomain],
  (usersState) => usersState.updatedUser
);
/**
 * Getting the  hotels selection list for dropdown  state into the component
 */
export const selectHotelsSelectionList = createSelector([selectDomain], (usersState) =>
  usersState.hotelSelectionList?.results?.map((data) => ({
    label: data?.name,
    id: data?._id,
    pmsDate: data?.pmsStartDate,
  }))
);
/**
 * Getting user by id state into the component
 */
export const selectCurrentUser = createSelector(
  [selectDomain],
  (usersState) => usersState.currentUser
);
/**
 * Getting custom landing page state into the component
 */
export const selectLandingPage = createSelector(
  [selectDomain],
  (usersState) => usersState.landingPage
);
/**
 * Getting is custom landing page updated state into the component
 */
export const selectIsLandingPageUpdated = createSelector(
  [selectDomain],
  (usersState) => usersState.isLandingPageUpdated
);
/**
 * Getting is stay dates preset list state into the component
 */
export const selectStayDatesPresetList = createSelector(
  [selectDomain],
  (usersState) => usersState.stayDatesPresetList
);
/**
 * Getting currency data state to user  component
 */
export const selectCurrencyData = createSelector(
  [selectDomain],
  (hotelState) => hotelState.currencyData
);
/**
 * Getting isOtpSent state to user component
 */
export const selectIsOtpSent = createSelector([selectDomain], (hotelState) => hotelState.isOtpSent);
/**
 * Getting isPhoneNumberVerified state to user component
 */
export const selectIsPhoneNumberVerified = createSelector(
  [selectDomain],
  (hotelState) => hotelState.isPhoneNumberVerified
);
/**
 * Getting advancedQueryInterpreter state to user component
 */
export const selectOpenAi = createSelector([selectDomain], (hotelState) => hotelState.advancedQueryInterpreter);
/**
 * Getting consent state to user component
 */
export const selectConsent = createSelector([selectDomain], (usersState) => usersState.consent);
/**
 * Getting interpreterAgent token usage state to user component
 */
export const selectInterpreterAgentTokenUsage = createSelector([selectDomain], (usersState) => usersState.interpreterAgent);
/**
 * Getting interpreterAgent token usage state to user component
 */
export const selectInterpreterAgentError = createSelector([selectDomain], (usersState) => usersState.interpreterAgentErrorMessage);